import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  AlertTriangle,
  HeartHandshake,
  Footprints,
  HeartPulse,
  Users,
  BookOpen,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';

/* Hub sections — short cards that link onward to the existing
 * SV content pages. Mirrors the DomesticAbuse page's section layout. */
const SECTIONS = [
  {
    id: 'process',
    eyebrow: 'PART 01',
    icon: Footprints,
    to: '/app/process',
    title: 'The judicial process',
    body:
      'Reporting · police investigation · CPS · charging · court · verdict · sentencing · appeals — every stage explained gently, so you know what to expect before you decide anything.',
    cta: 'Open the judicial process guide',
    testid: 'sv-hub-process',
  },
  {
    id: 'rights',
    eyebrow: 'PART 02',
    icon: ShieldCheck,
    to: '/app/rights',
    title: 'Your rights & ISVAs',
    body:
      "Lifetime anonymity, an ISVA by your side, special measures in court, your Victim Personal Statement, the Victims' Right to Review — 10 protections UK law guarantees you.",
    cta: 'Read your rights',
    testid: 'sv-hub-rights',
  },
  {
    id: 'trauma',
    eyebrow: 'PART 03',
    icon: HeartPulse,
    to: '/app/trauma',
    title: 'Trauma & healing',
    body:
      'The 5 trauma responses (freeze, fawn, fight, flight, flop), why your body did what it did, common after-effects, short explainer videos and grounding tools you can use right now.',
    cta: 'Understand the response',
    testid: 'sv-hub-trauma',
  },
  {
    id: 'support',
    eyebrow: 'PART 04',
    icon: Users,
    to: '/app/support?tab=sv',
    title: 'Specialist SV helplines',
    body:
      'Free confidential UK services — Rape Crisis, The Survivors Trust, SurvivorsUK, Mankind Initiative, Galop and SARCs. You do not need to have reported to access any of these.',
    cta: 'Browse helplines',
    testid: 'sv-hub-support',
  },
];

const NEXT_STEPS = [
  {
    to: '/app/guided-journal',
    title: 'Guided journal',
    body: 'A calm 8-step trauma-informed reflection — at your own pace, in this browser only.',
    icon: Sparkles,
    testid: 'sv-hub-guided-journal',
  },
  {
    to: '/app/journal',
    title: 'Private journal',
    body: 'A free, simple notebook. Stays in your browser. Never uploaded anywhere.',
    icon: BookOpen,
    testid: 'sv-hub-journal',
  },
];

function SectionCard({ s, idx }) {
  const Icon = s.icon;
  return (
    <article
      data-testid={s.testid}
      className="tf-card p-6 sm:p-7"
      style={{
        background: idx % 2 === 0
          ? 'linear-gradient(180deg, #FFFFFF 0%, #F8F4FB 100%)'
          : '#fff',
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: 'var(--tf-bg-secondary)',
            color: 'var(--tf-purple)',
            border: '1px solid var(--tf-purple-light)',
          }}
        >
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple)',
            }}
          >
            {s.eyebrow}
          </p>
          <h2
            className="font-heading mt-1"
            style={{
              fontSize: 'clamp(20px, 2.4vw, 23px)',
              color: 'var(--tf-text)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            {s.title}
          </h2>
          <p
            className="font-body mt-3"
            style={{
              fontSize: 13.5,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.7,
            }}
          >
            {s.body}
          </p>
          <Link
            to={s.to}
            data-testid={`${s.testid}-cta`}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold"
            style={{
              background: 'var(--tf-purple)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: 13,
              boxShadow: '0 8px 20px rgba(139,123,177,0.28)',
            }}
          >
            {s.cta}
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function SexualViolence() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="sv-hub-screen">
      <SEO
        title="Sexual Violence — UK Survivor Guide"
        description="A calm, trauma-informed UK hub for survivors of rape and sexual violence — the judicial process, your rights & ISVAs, trauma responses, healing, and specialist UK helplines. Anonymous. Free."
        path="/app/sexual-violence"
      />

      <p className="eyebrow">SEXUAL VIOLENCE — YOUR CALM GUIDE</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        You decide. You set the pace.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Whether you reported yesterday, ten years ago, or never — this calm guide belongs to
        you. Read what helps. Skip what doesn&apos;t. Come back any time. Choose your nation
        below so we can show you the right agencies, laws and specialist support.
      </p>

      <NationPicker testid="sv-nation-picker" />

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      {/* Reassurance card */}
      <section
        className="mt-6 tf-card p-5 sm:p-6"
        data-testid="sv-hub-reassurance"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
              boxShadow: '0 8px 22px rgba(139,123,177,0.28)',
            }}
          >
            <HeartHandshake size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-body font-bold"
              style={{
                fontSize: 10.5,
                letterSpacing: '0.22em',
                color: 'var(--tf-purple)',
              }}
            >
              BEFORE YOU READ ON
            </p>
            <h2
              className="font-heading mt-2"
              style={{
                fontSize: 'clamp(20px, 2.4vw, 23px)',
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              You are believed. It was not your fault.
            </h2>
            <p
              className="font-body mt-3"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              However you froze, fawned, complied or went quiet — that was your nervous
              system keeping you alive, not consent. What happened is not because of what
              you wore, what you said, what you didn&apos;t say, or who you trusted.
            </p>
            <p
              className="font-body mt-2"
              style={{
                fontSize: 13.5,
                color: 'var(--tf-text-secondary)',
                lineHeight: 1.7,
              }}
            >
              You do not have to report to deserve help.{' '}
              <strong style={{ color: 'var(--tf-purple-dark)' }}>You set the pace.</strong>
            </p>
            <p
              className="font-body mt-4"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.16em',
                color: 'var(--tf-purple-dark)',
              }}
            >
              YOU ARE BELIEVED · YOU ARE NOT ALONE
            </p>
          </div>
        </div>
      </section>

      {/* Crisis banner */}
      <section
        className="mt-6 rounded-2xl p-5 flex items-start gap-4"
        style={{ background: 'var(--tf-purple)' }}
        data-testid="sv-hub-crisis"
      >
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.2)' }}
        >
          <AlertTriangle size={20} color="#fff" />
        </div>
        <div className="flex-1">
          <p className="font-body font-bold" style={{ fontSize: 14, color: '#fff' }}>
            In immediate danger or just attacked?
          </p>
          <p
            className="font-body"
            style={{ fontSize: 13, color: 'rgba(255,255,255,0.95)', marginTop: 4, lineHeight: 1.55 }}
          >
            Call <a href="tel:999" className="underline" style={{ color: '#fff' }}>999</a>. For
            free 24/7 specialist support call Rape Crisis on{' '}
            <a href="tel:08088029999" className="underline" style={{ color: '#fff' }}>
              0808 802 9999
            </a>
            . You can also walk into a SARC for medical care without reporting.
          </p>
        </div>
      </section>

      <HelplinesStrip variant="sv" />

      {/* "Was that assault?" doorway to the "crossed a line" guide */}
      <Link
        to="/app/crossed-the-line"
        data-testid="sv-was-that-assault"
        className="mt-6 block tf-card tf-card-hover p-5"
        style={{
          textDecoration: 'none',
          color: 'var(--tf-text)',
          background: '#FFFDFA',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              IF YOU&apos;RE DOUBTING YOURSELF
            </p>
            <h3
              className="font-heading mt-1"
              style={{
                fontSize: 20,
                color: 'var(--tf-text)',
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
              }}
            >
              Was that assault?
            </h3>
            <p
              className="font-body mt-2"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
            >
              A calm, plain-language guide for anyone unsure whether what happened counts
              — with a section quietly answering the &ldquo;but…&rdquo; thoughts that
              stop survivors naming it.
            </p>
          </div>
          <ArrowRight size={18} color="var(--tf-purple-dark)" style={{ flexShrink: 0, marginTop: 6 }} />
        </div>
      </Link>

      {/* Section cards — the four parts of the SV path (nation-neutral doorways) */}
      <div className="mt-10 mb-4">
        <p className="eyebrow">WHAT THIS GUIDE COVERS</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 22, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Four calm parts — start anywhere
        </h2>
      </div>

      <div className="flex flex-col gap-4">
        {SECTIONS.map((s, i) => (
          <SectionCard key={s.id} s={s} idx={i} />
        ))}
      </div>

      {/* Next steps — journal options */}
      <div className="mt-12 mb-4">
        <p className="eyebrow">WHEN YOU&apos;RE READY</p>
        <h2
          className="font-heading mt-2"
          style={{ fontSize: 20, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
        >
          Something private, just for you
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {NEXT_STEPS.map((n) => {
          const Icon = n.icon;
          return (
            <Link
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className="tf-card tf-card-hover p-4 flex items-start gap-3"
              style={{ textDecoration: 'none', color: 'var(--tf-text)' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
              >
                <Icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="font-heading"
                  style={{
                    fontSize: 15,
                    color: 'var(--tf-text)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {n.title}
                </h3>
                <p
                  className="font-body mt-1"
                  style={{
                    fontSize: 12.5,
                    color: 'var(--tf-text-secondary)',
                    lineHeight: 1.5,
                  }}
                >
                  {n.body}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Source footnote */}
      <div
        className="mt-10 p-5 rounded-2xl"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
      >
        <p
          className="font-body"
          style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
        >
          Information adapted from Rape Crisis (England & Wales) and Rape Crisis Scotland, the Crown Prosecution Service and COPFS, the Victims&apos; Codes, NHS, Mind, PTSD UK and gov.uk / mygov.scot. This
          is not legal advice — for legal advice please speak to a solicitor, your ISVA (E&amp;W) or your Rape Crisis Scotland advocacy worker.
        </p>
      </div>
    </div>
  );
}
